import { AlertTriangle, ImageOff, Pencil } from 'lucide-react'
import {
  colorByName, colorImg, presetImg, patternImg,
  RETAINER_PRESETS, RETAINER_PATTERNS, stickerById, stickerImg,
} from '../../data/retainerCatalog.js'
import { Badge, Button } from '../../kit'

// รายละเอียดรีเทนเนอร์ — the spec, READ OUT (2026-08-17, Ram: show it without having to
// open the builder). It used to live only behind แก้ไขประเภทงาน & รีเทนเนอร์, so the one
// thing that says what the piece actually IS — pattern, colours, stickers, หุ้มลวด — was
// invisible on the record it belongs to. The builder is still how you change it; this is
// how you read it.
//
// Renders nothing when there is no spec, so an outside-lab record that was never specced
// here does not grow an empty card.
export default function RetainerDetail({ productionType, retainer, onEdit, dirty = false }) {
  const r = retainer || {}
  const sides = [r.retainer_upper && 'บน', r.retainer_lower && 'ล่าง'].filter(Boolean).join(' + ')
  const colors = (Array.isArray(r.retainer_color) ? r.retainer_color : []).filter(Boolean)
  const stickers = (Array.isArray(r.retainer_addon) ? r.retainer_addon : []).filter((x) => typeof x === 'string')
  const rub = r.retainer_rubber || {}
  const rubSides = [
    rub.upper && `บน${rub.upper_color ? ` (${rub.upper_color})` : ''}`,
    rub.lower && `ล่าง${rub.lower_color ? ` (${rub.lower_color})` : ''}`,
  ].filter(Boolean).join(' · ')

  const preset = r.retainer_preset ? RETAINER_PRESETS.find((p) => p.name === r.retainer_preset) : null
  const pattern = r.retainer_pattern ? RETAINER_PATTERNS.find((p) => p.name === r.retainer_pattern) : null

  // "Is there a spec?" is about the RETAINER, not about production_type — a lab can carry
  // production_type "retainer" and nothing else, which is exactly what a piece that has
  // not been designed yet looks like. Judging emptiness on productionType too made that
  // record render as a grid of em-dashes instead of saying what was actually true.
  const specEmpty = !sides && !r.retainer_type && !r.retainer_preset && !r.retainer_pattern
    && !colors.length && !stickers.length && !rubSides && !r.retainer_note
  if (specEmpty && !productionType && !onEdit) return null

  // The one picture that says what the piece looks like. A preset ENDS the builder's flow
  // (upstream blanks pattern/colour/sticker when one is set), so it wins; otherwise the
  // pattern. Shown big, because a name like "แนวตั้งเฉียง 3 สี" is not a design.
  const heroSrc = preset ? presetImg(preset.slug) : (pattern ? patternImg(pattern.slug) : '')
  const heroLabel = r.retainer_preset || (pattern?.code ? `${pattern.code} · ${r.retainer_pattern}` : r.retainer_pattern)
  const heroKind = r.retainer_preset ? 'ลายสำเร็จ' : 'ลาย'

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <span className="text-[12px] font-medium uppercase tracking-wide text-slate-400">รายละเอียดรีเทนเนอร์</span>
        <div className="flex items-center gap-2 shrink-0">
          {/* The design is in the form, not in Xano, until the drawer's own บันทึก runs.
              Worded as an instruction rather than a status: "ยังไม่บันทึก" alone reads as a
              failure when the user has just pressed a button that said บันทึก. */}
          {dirty && (
            <Badge tone="amber"><AlertTriangle size={11} /> ยังไม่บันทึก — กด บันทึก ด้านล่าง</Badge>
          )}
          {onEdit && (
            <Button size="sm" variant="soft" icon={Pencil} onClick={onEdit}>แก้ไข</Button>
          )}
        </div>
      </div>

      {specEmpty ? (
        <div className="flex items-center gap-3">
          <span className="w-14 h-14 rounded-lg bg-slate-50 ring-1 ring-slate-200 flex items-center justify-center shrink-0">
            <ImageOff size={18} className="text-slate-300" />
          </span>
          <div className="min-w-0">
            <div className="text-[13.5px] text-slate-600">ยังไม่ได้ตั้งค่ารีเทนเนอร์</div>
            <div className="text-[12px] text-slate-400">
              ประเภทงาน: <span className="text-slate-600">{productionType || '—'}</span>
              {onEdit && ' · กด แก้ไข เพื่อออกแบบชิ้นงาน'}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {/* The picture and the facts that name it, side by side. */}
          <div className="flex items-start gap-3">
            {heroSrc
              ? <Thumb src={heroSrc} size={72} />
              : <span className="w-[72px] h-[72px] rounded-lg bg-slate-50 ring-1 ring-slate-200 shrink-0" />}
            <div className="min-w-0 flex-1 grid grid-cols-2 gap-x-3 gap-y-2">
              <Cell label="ประเภทงาน" value={productionType || '—'} />
              <Cell label="ชิ้นงาน" value={sides || '—'} />
              {r.retainer_type && <Cell label="ชนิด" value={r.retainer_type} />}
              {rubSides && <Cell label="หุ้มลวด" value={rubSides} />}
            </div>
          </div>
          {heroLabel && (
            <div className="text-[12px] text-slate-500">
              <span className="text-slate-400">{heroKind}: </span>
              <span className="text-slate-700">{heroLabel}</span>
            </div>
          )}

          {colors.length > 0 && (
            <Row label="สี">
              <div className="flex items-center gap-2 flex-wrap">
                {colors.map((name, i) => {
                  const c = colorByName(name)
                  return (
                    <span key={`${name}-${i}`} className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 ring-1 ring-slate-200 pl-1 pr-2.5 py-0.5">
                      {c
                        ? <img src={colorImg(c.slug)} alt="" className="w-6 h-6 rounded-full object-cover" />
                        : <span className="w-6 h-6 rounded-full bg-slate-200" />}
                      <span className="text-[12.5px] text-slate-700">{name}</span>
                    </span>
                  )
                })}
              </div>
            </Row>
          )}
          {stickers.length > 0 && (
            <Row label="สติกเกอร์">
              <div className="flex items-center gap-2 flex-wrap">
                {stickers.map((id) => {
                  const st = stickerById(id)
                  return (
                    <span key={id} className="inline-flex items-center gap-1.5 rounded-lg bg-slate-50 ring-1 ring-slate-200 px-1.5 py-1">
                      <img src={stickerImg(id)} alt="" className="w-8 h-8 object-contain" />
                      <span className="text-[12px] text-slate-600">{st?.name || id}</span>
                    </span>
                  )
                })}
              </div>
            </Row>
          )}
          {r.retainer_note && (
            <Row label="โน้ต">
              <span className="text-[13px] text-slate-700 whitespace-pre-wrap">{r.retainer_note}</span>
            </Row>
          )}
        </div>
      )}
    </section>
  )
}

function Cell({ label, value }) {
  return (
    <div>
      <div className="text-[12px] text-slate-400 mb-0.5">{label}</div>
      <div className="text-[13.5px] text-slate-800">{value}</div>
    </div>
  )
}
function Row({ label, children }) {
  return (
    <div>
      <div className="text-[12px] text-slate-400 mb-1">{label}</div>
      <div className="flex items-center gap-2 flex-wrap">{children}</div>
    </div>
  )
}
// Self-hosted under public/lab; a missing file hides the thumb rather than showing a
// broken-image glyph next to the name that already says what it is.
function Thumb({ src, size = 32 }) {
  if (!src) return null
  return <img src={src} alt="" width={size} height={size}
    style={{ width: size, height: size }}
    className="rounded-lg object-cover bg-slate-50 ring-1 ring-slate-200 shrink-0"
    onError={(e) => { e.currentTarget.style.visibility = 'hidden' }} />
}
